import styles from './RimFilter.module.scss'
import React, { useEffect, useMemo, useState } from 'react'
import FilterButtons from '../FilterButtons/FilterButtons'
import { FilterCondition, FilterType, useFilter } from '@/utils/hooks/useFilter'
import { usePrevious } from '@/utils/hooks/usePrevious'
import FilterBar from '../FilterBar/FilterBar'
import FilterCheckboxes from '../FilterCheckboxes/FilterCheckboxes'
import { useGetColorsQuery } from '@/api/colors'
import NameValue from '@/types/NameValue'
import FilterSlider from '../FilterSlider/FilterSlider'
import Checkbox from '@/components/inputs/Checkbox/Checkbox'
import { useTranslation } from 'next-i18next'
import { useAppSelector } from '@/utils/hooks/useAppSelector'
import { selectCurrency } from '@/features/app/appSlice'
import { ArticleModel } from '@/types/api'
import FilterBox from '../FilterBox/FilterBox'
import Button from '@/components/buttons/Button/Button'
import FilterChangeHandler from '@/types/FilterChangeHandler'

type Props = {
  items: ArticleModel[]
  onFilterChange: FilterChangeHandler<ArticleModel>
}

const RimFilter = (props: Props) => {
  const prevItems = usePrevious(props.items)
  const [selectedFilter, setSelectedFilter] = useState('size')
  const { data } = useGetColorsQuery()
  const { t } = useTranslation(['common', 'shop'])
  const currency = useAppSelector(selectCurrency)

  /**
   * Custom filter conditions
   */
  const filterConditons: FilterCondition<ArticleModel>[] = useMemo(() => [
    {
      property: 'regularPrice',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.regularPrice !== null
    },
    {
      group: 'stock',
      property: 'inStock',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => (
        item.quantityAvailable && !item.deliveryTime && !item.estimatedDeliveryDate)
        ||
        (item.quantityAvailable < 4 && item.quantityAvailable > 0 && item.estimatedDeliveryDate !== null)
    },
    {
      group: 'stock',
      property: 'onPurchase',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.estimatedDeliveryDate !== null
    },
    {
      group: 'stock',
      property: 'toOrder',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.deliveryTime !== null
    },
    {
      group: 'stock',
      property: 'onPurchase',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.estimatedDeliveryDate !== null
    },
    {
      property: 'diameter',
      filterType: FilterType.Checkbox,
    },
    {
      property: 'colorId',
      filterType: FilterType.Checkbox,
    }
  ], [])

  const {
    getSelectedValues,
    getValuesByPropery,
    saveSelectedValues,
    clearSelectedValues,
    isPropertySelected,
    filterItems,
    selectedValues,
    getFilterQuery,
    setFilterFromQuery
  } = useFilter(props.items, filterConditons)

  const prevSelectedValues = usePrevious(selectedValues)

  /**
   * Items to show in filter bar
   */
  const filterBarItems = [
    {
      property: 'size',
      placeholder: t('shop:size'),
      value: () => {
        let diamaters = [...getSelectedValues('diameter')].sort((a, b) => a - b) ?? []

        if (diamaters.length === 0)
          return ''

        return diamaters.join(', ') + 'Tum'
      }
    },
    {
      property: 'stockStatus',
      placeholder: t('shop:stockStatus'),
      value: () => ''
    },
    {
      property: 'color',
      placeholder: t('shop:color'),
      value: () => {
        const colorIds = getSelectedValues('colorId')
        const colorNames = data?.filter((color) => colorIds.includes(color.id)).map((color) => color.name) ?? []
        return colorNames.join(', ')
      }
    },
    {
      property: 'brand',
      placeholder: t('shop:brand'),
      value: () => {
        const brandNames = getSelectedValues('brandName')
        return brandNames.join(', ')
      }
    },
    {
      property: 'price',
      placeholder: t('shop:price'),
      value: () => {
        const prices = getSelectedValues('price')

        if (prices.length === 0)
          return ''

        return `${prices[0]} - ${prices[1]} ${currency}`
      }
    },
    {
      property: 'properties',
      placeholder: t('shop:properties'),
      value: () => ''
    },
  ]

  const onFilterValueChanged = (property: string, value: any, filterType: FilterType) => {
    saveSelectedValues({ property, value, filterType })
  }

  const onCheckboxChangedHandler = (checked: boolean, name: string) => {
    onFilterValueChanged(name, checked, FilterType.Boolean)
  }

  const onClickFilterType = (property: string) => {
    setSelectedFilter(property)
  }

  const onClearFilter = () => {
    clearSelectedValues()
    setSelectedFilter('size')
  }

  const getDiameterText = (value: string) => (
    <div className={styles.inchButton}>
      <div>{value}</div>
      <div className={styles.surfix}>Tum</div>
    </div>
  )

  const colors = useMemo(() => {
    const itemColorIds = getValuesByPropery('colorId')
    // filter out values based on itemColorIds
    const filtered = data?.filter((color) => itemColorIds.includes(color.id)) ?? []

    return filtered.map<NameValue>((color) => ({ name: color.name, value: color.id }))
  }, [data, getValuesByPropery])

  /**
   * Filter items when selected values change
   */
  useEffect(() => {
    if (prevSelectedValues !== selectedValues) {
      props.onFilterChange(filterItems(), getFilterQuery())
    }
  }, [filterItems, getFilterQuery, prevSelectedValues, props, selectedValues])

  /**
   * Clear selected values when items change
   */
  useEffect(() => {
    if (prevItems?.length !== props.items.length) {
      clearSelectedValues()
    }
  }, [props.items, clearSelectedValues, prevItems])

  useEffect(() => {
    setFilterFromQuery()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      {props.items.length > 0 && (
        <FilterBar
          onClick={onClickFilterType}
          selected={selectedFilter}
          items={filterBarItems}
        />
      )}
      {selectedFilter === 'size' && (
        <>
          <FilterButtons
            items={props.items || []}
            property={'diameter'}
            onClick={onFilterValueChanged}
            selected={getSelectedValues('diameter')}
            valueText={getDiameterText}
          />
          <div className={styles.sliders}>
            <FilterSlider
              label={t('shop:width')}
              items={props.items || []}
              property={'width'}
              step={0.5}
              showStartAndEnd={true}
              value={getSelectedValues('width')}
              onChange={onFilterValueChanged}
            />
            <FilterSlider
              label={t('shop:offset')}
              items={props.items || []}
              property={'offset'}
              showStartAndEnd={true}
              valueSuffix={'mm'}
              value={getSelectedValues('offset')}
              onChange={onFilterValueChanged}
            />
          </div>
        </>
      )}
      {selectedFilter === 'color' && (
        <FilterCheckboxes
          items={props.items || []}
          values={colors}
          property={'colorId'}
          onClick={onFilterValueChanged}
          selected={getSelectedValues('colorId')}
        />
      )}
      {selectedFilter === 'brand' && (
        <FilterCheckboxes
          items={props.items || []}
          property={'brandName'}
          onClick={onFilterValueChanged}
          selected={getSelectedValues('brandName')}
        />
      )}
      {selectedFilter === 'price' && (
        <>
          <FilterSlider
            label={t('shop:price')}
            items={props.items || []}
            property={'price'}
            showStartAndEnd={true}
            value={getSelectedValues('price')}
            onChange={onFilterValueChanged}
          />
          <Checkbox
            label={t('shop:showOnlyDiscounted')}
            name={'regularPrice'}
            checked={isPropertySelected('regularPrice')}
            onChange={onCheckboxChangedHandler}
          />
        </>
      )}
      {selectedFilter === 'stockStatus' && (
        <FilterBox>
          <Checkbox
            label={t('common:stock.inStock')}
            name={'inStock'}
            checked={isPropertySelected('inStock')}
            onChange={onCheckboxChangedHandler}
          />
          <Checkbox
            label={t('common:stock.toOrder')}
            name={'toOrder'}
            checked={isPropertySelected('toOrder')}
            onChange={onCheckboxChangedHandler}
          />
          <Checkbox
            label={t('common:stock.onPurchase')}
            name={'onPurchase'}
            checked={isPropertySelected('onPurchase')}
            onChange={onCheckboxChangedHandler}
          />
        </FilterBox>
      )}
      {selectedFilter === 'properties' && (
        <FilterBox>
          <Checkbox
            label={t('shop:winterApproved')}
            name={'isWinterApproved'}
            checked={isPropertySelected('isWinterApproved')}
            onChange={onCheckboxChangedHandler}
          />
        </FilterBox>
      )}
      <Button
        onClick={onClearFilter}
        label={t('shop:resetFilters')}
        disabled={selectedValues.length === 0}
      />
    </div>
  )
}

export default RimFilter